import axios from 'axios';
import {
  Form,
  FormProps,
  Input,
  Modal,
  ModalProps,
  Space,
  Typography,
  Upload,
  UploadFile,
  UploadProps,
  message
} from 'antd';
import { FormItem } from './FormItem';
import { ISupplementaryMaterial } from 'interfaces/supplementary_materials';
import { InboxOutlined } from '@ant-design/icons';
import { useCallback, useState } from 'react';
import { useCreate } from '@refinedev/core';

const { Dragger, LIST_IGNORE } = Upload;

interface SupplementarMaterialUploadModalProps {
  modalProps: ModalProps;
  formProps: FormProps;
  closeModal: () => void;
  parentReference: string;
  parentKind?: string;
  getSavedItems: () => void;
  okText?: string;
  successMessage?: string;
  restrictToPdf?: boolean;
}

interface Asset {
  url: string;
  upload_url: string;
  download_url: string;
}

export const isPdfFile = (file: { type?: string; name?: string }) => {
  const type = (file.type ?? '').toLowerCase();
  const name = (file.name ?? '').toLowerCase();

  if (type === 'application/pdf') {
    return true;
  }

  if (!type || type === 'application/octet-stream') {
    return name.endsWith('.pdf');
  }

  return false;
};

const fileExtension = (name?: string) =>
  (name ?? '').split('.').pop()?.toLowerCase() ?? '';

export const SupplementarMaterialUploadModal = ({
  modalProps,
  formProps,
  parentReference,
  parentKind = 'classroom',
  getSavedItems,
  closeModal,
  okText = 'Salvar',
  successMessage = 'Material complementar enviado com sucesso!',
  restrictToPdf = true
}: SupplementarMaterialUploadModalProps) => {
  const { mutate } = useCreate<Asset>();
  const [file, setFile] = useState<UploadFile | null>();
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const { mutateAsync: createSupplementaryMaterial } =
    useCreate<ISupplementaryMaterial>();

  const createContent = (name: string) =>
    new Promise<Asset>((resolve, reject) => {
      mutate(
        {
          resource: 'content_files',
          values: {
            type: 'content_supplementary_material',
            extension: restrictToPdf ? 'pdf' : fileExtension(name)
          },
          successNotification: false,
          errorNotification: () => ({
            type: 'error',
            description: 'Ops!',
            message:
              'Não foi possível realizar o upload do arquivo. Tente novamente.'
          })
        },
        {
          onSuccess: ({ data }) => resolve(data),
          onError: error => reject(error)
        }
      );
    });

  const upload = async (fileToUpload: UploadFile | File | Blob) => {
    const rawFile =
      fileToUpload instanceof Blob ? fileToUpload : fileToUpload.originFileObj;

    if (!rawFile) {
      return;
    }

    setIsUploading(true);
    setFileUrl('');

    try {
      // File também é um Blob, mas carrega o nome (e portanto a extensão).
      const name = (fileToUpload as { name?: string }).name ?? '';
      const { url, upload_url } = await createContent(name);
      await axios.put(upload_url, rawFile);
      setFileUrl(url);
      message.success('Upload de arquivo realizado com sucesso');
    } catch {
      const errorMessage =
        'Não foi possível realizar o upload do arquivo. Tente novamente.';
      setFileUrl('');
      setFileError(errorMessage);
      message.error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const props: UploadProps = {
    name: 'supplementary_material',
    accept: restrictToPdf ? 'application/pdf' : undefined,
    fileList: file ? [file] : [],
    onRemove: () => {
      setFile(null);
      setFileError(null);
      setFileUrl('');
    },
    beforeUpload: newFile => {
      const { name } = newFile;

      if (restrictToPdf && !isPdfFile(newFile)) {
        const errorMessage = `${name} não é um PDF. Apenas arquivos PDF são permitidos.`;
        setFileError(errorMessage);
        message.error(errorMessage);
        return LIST_IGNORE;
      }

      setFileError(null);
      setFile(newFile);
      setFileUrl('');
      return false;
    },
    onChange: info => {
      if (info.file.status === 'removed') {
        return;
      }

      if (restrictToPdf && !isPdfFile(info.file)) {
        return;
      }

      const rawFile = info.file.originFileObj ?? info.file;
      upload(rawFile);
    }
  };

  const handleFinish = useCallback(async () => {
    if (!file || (restrictToPdf && !isPdfFile(file))) {
      const errorMessage = restrictToPdf
        ? 'Selecione um arquivo PDF para publicar.'
        : 'Selecione um arquivo para publicar.';
      setFileError(errorMessage);
      message.error(errorMessage);
      return;
    }

    if (isUploading || !fileUrl) {
      const errorMessage = 'Aguarde o término do upload do arquivo.';
      setFileError(errorMessage);
      message.error(errorMessage);
      return;
    }

    setFileError(null);

    const item = await formProps.form?.validateFields();

    const values = {
      title: item.name,
      url: fileUrl,
      parent_reference: parentReference,
      parent_kind: parentKind
    };

    await createSupplementaryMaterial({
      resource: 'supplementary_materials',
      values,
      successNotification: () => ({
        message: successMessage,
        type: 'success'
      }),
      errorNotification: () => {
        return {
          message:
            'Erro ao enviar o material. Verifique o formato e tente novamente.',
          description: 'Algo deu errado!',
          type: 'error'
        };
      }
    });

    getSavedItems();
    setFile(null);
    setFileError(null);
    setFileUrl('');
    formProps.form?.resetFields();
    closeModal();
  }, [
    formProps,
    parentReference,
    parentKind,
    file,
    fileUrl,
    isUploading,
    getSavedItems,
    closeModal,
    createSupplementaryMaterial,
    successMessage,
    restrictToPdf
  ]);

  return (
    <Modal
      {...modalProps}
      title="Upload de um Material"
      okText={okText}
      cancelText="Cancelar"
      className="max-w-[700px]"
      confirmLoading={isUploading || modalProps.confirmLoading}
      okButtonProps={{
        ...modalProps.okButtonProps,
        disabled:
          !fileUrl || isUploading || Boolean(modalProps.okButtonProps?.disabled)
      }}
    >
      <Space direction="vertical" className="w-full gap-4">
        <Dragger {...props}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Clique ou arraste para adicionar o arquivo
          </p>
          <p className="ant-upload-hint">
            {restrictToPdf && (
              <>
                Formato suportado: PDF
                <br />
              </>
            )}
            {/* 5 GB é o limite real imposto pelo próprio S3 para PutObject
                (upload single-part via presigned URL), usado neste fluxo em
                admin/content_file.rb#upload_url. Não há validação de
                tamanho no Atlas nem no client — arquivos maiores que isso
                falham com EntityTooLarge no S3. */}
            Tamanho máximo: 5 GB
          </p>
        </Dragger>
        {fileError && (
          <Typography.Text type="danger">{fileError}</Typography.Text>
        )}

        <Form {...formProps} onFinish={handleFinish}>
          <Form.Item
            name="pdf_file"
            hidden
            rules={[
              {
                validator: async () => {
                  if (!file || (restrictToPdf && !isPdfFile(file))) {
                    const errorMessage = restrictToPdf
                      ? 'Selecione um arquivo PDF para publicar.'
                      : 'Selecione um arquivo para publicar.';
                    setFileError(errorMessage);
                    throw new Error(errorMessage);
                  }
                }
              }
            ]}
          >
            <Input />
          </Form.Item>
          <Typography.Text>Dê um nome para este arquivo</Typography.Text>
          <FormItem
            name="name"
            rules={[
              { required: true, message: 'O nome do arquivo é obrigatório' }
            ]}
          >
            <Input placeholder="Ex.: material complementar" />
          </FormItem>
        </Form>
      </Space>
    </Modal>
  );
};
